import React, { useState, useEffect, useMemo } from 'react';
import { menusAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Plus, Edit, Trash2, ChevronRight, Menu as MenuIcon, LayoutDashboard, 
  ClipboardList, Users, Settings, Shield, Sliders, Building2, FolderKanban,
  GripVertical, Save, X, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const iconMap = {
  'LayoutDashboard': LayoutDashboard,
  'ClipboardList': ClipboardList,
  'Users': Users,
  'Menu': MenuIcon,
  'Settings': Settings,
  'Shield': Shield,
  'Sliders': Sliders,
  'Building2': Building2,
  'FolderKanban': FolderKanban,
};

const iconOptions = [
  { value: 'LayoutDashboard', label: 'Dashboard' },
  { value: 'ClipboardList', label: 'Lista' },
  { value: 'Users', label: 'Usuarios' },
  { value: 'Menu', label: 'Menú' },
  { value: 'Settings', label: 'Configuración' },
  { value: 'Shield', label: 'Seguridad' },
  { value: 'Sliders', label: 'Ajustes' },
  { value: 'Building2', label: 'Edificio' },
  { value: 'FolderKanban', label: 'Carpeta' },
];

const rolesOptions = [
  { value: 'admin', label: 'Administrador' },
  { value: 'jefe_postventa', label: 'Jefe Postventa' },
  { value: 'supervisor', label: 'Supervisor' },
];

// Sortable Menu Item Component
const SortableMenuItem = ({ menu, depth, onEdit, onDelete, canEdit, canDelete, getRolesBadge }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: menu.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginLeft: `${depth * 16}px`,
    opacity: isDragging ? 0.5 : 1,
  };

  const bgColor = depth === 0 ? 'bg-white' : depth === 1 ? 'bg-[#F5F5F5]' : 'bg-[#E8E8E7]';
  const IconComponent = iconMap[menu.icono] || MenuIcon;

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div
        className={`p-2 sm:p-3 lg:p-4 border border-[#E8E8E7] rounded-lg ${bgColor} hover:shadow-md transition-all ${isDragging ? 'shadow-lg ring-2 ring-[#9d8272]' : ''}`}
        data-testid="menu-item"
      >
        <div className="flex items-center justify-between gap-2">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-[#E8E8E7] rounded touch-none"
            data-testid="drag-handle"
          >
            <GripVertical size={18} className="text-[#888888]" />
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {depth > 0 && <ChevronRight size={14} className="text-[#888888] hidden sm:block" />}
              <IconComponent size={16} className="sm:w-[18px] sm:h-[18px] text-[#9d8272]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 sm:mb-1 flex-wrap">
                <h4 className="font-semibold text-[#2D2D2D] text-sm sm:text-base truncate">{menu.nombre}</h4>
                <Badge className="bg-[#c5b8a2] text-[#2D2D2D] border-0 text-[10px] sm:text-xs">
                  N{menu.nivel}
                </Badge>
              </div>
              <p className="text-xs sm:text-sm text-[#5A5A5A] truncate">
                <span className="font-mono text-[10px] sm:text-xs">{menu.ruta || '(sin ruta)'}</span>
              </p>
              <p className="text-[10px] sm:text-xs text-[#888888] truncate hidden sm:block">
                Roles: {getRolesBadge(menu.roles_permitidos)}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] sm:text-xs text-[#888888] whitespace-nowrap mr-2">#{menu.orden}</span>
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(menu)}
                className="h-8 w-8 p-0 hover:bg-[#E8E8E7]"
                data-testid="edit-menu-button"
              >
                <Edit size={14} className="text-[#9d8272]" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(menu)}
                className="h-8 w-8 p-0 hover:bg-red-50"
                data-testid="delete-menu-button"
              >
                <Trash2 size={14} className="text-red-500" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Menus = () => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuToDelete, setMenuToDelete] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeId, setActiveId] = useState(null);
  
  const { user } = useAuth();
  const { isAdmin, canCreate, canView, canEdit, canDelete } = usePermissions();

  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    ruta: '',
    icono: 'Menu',
    nivel: 1,
    padre_id: '',
    roles_permitidos: ['admin'],
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    if (!canView('/menus') && !isAdmin()) {
      toast.error('No tiene permisos para ver menús');
      setLoading(false);
      return;
    }

    try {
      const response = await menusAPI.getAll();
      setMenus(response.data || []);
    } catch (error) {
      toast.error('Error al cargar menús');
    } finally {
      setLoading(false);
    }
  };

  // Organize menus into hierarchy
  const organizedMenus = useMemo(() => {
    const parentMenus = menus.filter(m => !m.padre_id).sort((a, b) => a.orden - b.orden);
    const childMenus = menus.filter(m => m.padre_id);

    return parentMenus.map(parent => ({
      ...parent,
      children: childMenus
        .filter(child => child.padre_id === parent.id)
        .sort((a, b) => a.orden - b.orden)
    }));
  }, [menus]);

  // Flatten for drag & drop
  const flatMenuList = useMemo(() => {
    const result = [];
    organizedMenus.forEach(parent => {
      result.push({ ...parent, depth: 0 });
      if (parent.children) {
        parent.children.forEach(child => {
          result.push({ ...child, depth: 1 });
        });
      }
    });
    return result;
  }, [organizedMenus]);

  const getTotalMenuCount = () => menus.length;

  const getRolesBadge = (roles) => {
    const roleLabels = {
      admin: 'Admin',
      jefe_postventa: 'Jefe',
      supervisor: 'Supervisor',
    };
    return (roles || []).map(r => roleLabels[r] || r).join(', ');
  };

  // Drag handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      const oldIndex = flatMenuList.findIndex(m => m.id === active.id);
      const newIndex = flatMenuList.findIndex(m => m.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Update the menus array with new order
        const newFlatList = arrayMove(flatMenuList, oldIndex, newIndex);
        
        // Recalculate orden for all items
        const updatedMenus = menus.map(menu => {
          const newPos = newFlatList.findIndex(m => m.id === menu.id);
          return { ...menu, orden: newPos + 1 };
        });
        
        setMenus(updatedMenus);
        setHasChanges(true);
      }
    }
  };

  // Save reorder changes
  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const items = menus.map(m => ({
        id: m.id,
        orden: m.orden,
        padre_id: m.padre_id
      }));
      
      await menusAPI.reorder(items);
      toast.success('Orden guardado correctamente');
      setHasChanges(false);
      await loadMenus();
    } catch (error) {
      toast.error('Error al guardar el orden');
    } finally {
      setSaving(false);
    }
  };

  // Form handlers
  const resetForm = () => {
    setFormData({
      nombre: '',
      ruta: '',
      icono: 'Menu',
      nivel: 1,
      padre_id: '',
      roles_permitidos: ['admin'],
    });
    setEditingMenu(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleOpenEdit = (menu) => {
    setEditingMenu(menu);
    setFormData({
      nombre: menu.nombre,
      ruta: menu.ruta || '',
      icono: menu.icono,
      nivel: menu.nivel,
      padre_id: menu.padre_id || '',
      roles_permitidos: menu.roles_permitidos || ['admin'],
    });
    setShowDialog(true);
  };

  const handleOpenDelete = (menu) => {
    setMenuToDelete(menu);
    setShowDeleteDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        padre_id: formData.padre_id || null,
        orden: editingMenu ? editingMenu.orden : menus.length + 1,
      };

      if (editingMenu) {
        await menusAPI.update(editingMenu.id, data);
        toast.success('Menú actualizado correctamente');
      } else {
        await menusAPI.create(data);
        toast.success('Menú creado correctamente');
      }

      setShowDialog(false);
      resetForm();
      await loadMenus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar menú');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!menuToDelete) return;
    
    setSaving(true);
    try {
      await menusAPI.delete(menuToDelete.id);
      toast.success('Menú eliminado correctamente');
      setShowDeleteDialog(false);
      setMenuToDelete(null);
      await loadMenus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar menú');
    } finally {
      setSaving(false);
    }
  };

  const handleRolesChange = (role) => {
    setFormData(prev => {
      const roles = prev.roles_permitidos.includes(role)
        ? prev.roles_permitidos.filter(r => r !== role)
        : [...prev.roles_permitidos, role];
      return { ...prev, roles_permitidos: roles.length > 0 ? roles : ['admin'] };
    });
  };

  // Permissions
  const canCreateMenu = canCreate('/menus') || isAdmin();
  const canEditMenu = canEdit('/menus') || isAdmin();
  const canDeleteMenu = canDelete('/menus') || isAdmin();
  const canViewMenu = canView('/menus') || isAdmin();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9d8272]"></div>
      </div>
    );
  }

  if (!canViewMenu) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="bg-white border border-[#E8E8E7] shadow-sm p-6">
          <p className="text-[#888888]">No tiene permisos para ver esta página</p>
        </Card>
      </div>
    );
  }

  const parentMenusForSelect = menus.filter(m => m.nivel === 1);
  const activeMenu = activeId ? flatMenuList.find(m => m.id === activeId) : null;

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="menus-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Menús
          </h1>
          <p className="text-sm lg:text-lg text-[#5A5A5A] mt-1 hidden sm:block">Arrastra para reordenar • CRUD completo</p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button
              onClick={handleSaveOrder}
              disabled={saving}
              className="bg-[#8c926f] hover:bg-[#7a8060] text-white"
              data-testid="save-order-button"
            >
              <Save size={16} className="mr-1 sm:mr-2" />
              {saving ? 'Guardando...' : 'Guardar Orden'}
            </Button>
          )}
          {canCreateMenu && (
            <Button
              onClick={handleOpenCreate}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
              data-testid="create-menu-button"
            >
              <Plus size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Nueva Opción</span>
              <span className="sm:hidden">Nuevo</span>
            </Button>
          )}
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-[#9d8272]/10 to-[#8c926f]/10 border-l-4 border-[#9d8272] hidden sm:block">
        <CardContent className="p-4 lg:p-6">
          <h3 className="font-semibold text-[#2D2D2D] mb-2 text-sm lg:text-base">Sistema de Menús Dinámicos</h3>
          <p className="text-xs lg:text-sm text-[#5A5A5A]">
            Arrastra y suelta los menús para cambiar su orden. Los cambios se guardan al presionar "Guardar Orden".
          </p>
        </CardContent>
      </Card>

      {/* Menu Tree with Drag & Drop */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm">
        <CardHeader className="p-3 sm:p-4 lg:p-6">
          <CardTitle className="text-base sm:text-lg lg:text-xl font-semibold text-[#2D2D2D]">
            Estructura de Menús ({getTotalMenuCount()} opciones)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 lg:p-6 pt-0">
          {flatMenuList.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={flatMenuList.map(m => m.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-0">
                  {flatMenuList.map(menu => (
                    <SortableMenuItem
                      key={menu.id}
                      menu={menu}
                      depth={menu.depth}
                      onEdit={handleOpenEdit}
                      onDelete={handleOpenDelete}
                      canEdit={canEditMenu}
                      canDelete={canDeleteMenu}
                      getRolesBadge={getRolesBadge}
                    />
                  ))}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeMenu ? (
                  <div className="p-3 bg-white border-2 border-[#9d8272] rounded-lg shadow-xl">
                    <div className="flex items-center gap-2">
                      <GripVertical size={18} className="text-[#9d8272]" />
                      <span className="font-semibold text-[#2D2D2D]">{activeMenu.nombre}</span>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            <div className="text-center py-12">
              <MenuIcon size={48} className="mx-auto mb-4 text-[#888888] opacity-50" />
              <p className="text-[#888888]">No hay opciones de menú configuradas</p>
              {canCreateMenu && (
                <Button
                  onClick={handleOpenCreate}
                  variant="outline"
                  className="mt-4 border-[#9d8272] text-[#9d8272]"
                >
                  Crear Primera Opción
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:gap-6">
        <Card className="bg-white border-l-4 border-[#9d8272] shadow-sm">
          <CardContent className="p-3 sm:p-4 lg:p-6">
            <p className="text-[10px] sm:text-xs lg:text-sm text-[#888888] mb-1">Nivel 1</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#2D2D2D]">
              {menus.filter(m => m.nivel === 1).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-[#8c926f] shadow-sm">
          <CardContent className="p-3 sm:p-4 lg:p-6">
            <p className="text-[10px] sm:text-xs lg:text-sm text-[#888888] mb-1">Nivel 2</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#2D2D2D]">
              {menus.filter(m => m.nivel === 2).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-[#c5b8a2] shadow-sm">
          <CardContent className="p-3 sm:p-4 lg:p-6">
            <p className="text-[10px] sm:text-xs lg:text-sm text-[#888888] mb-1">Nivel 3</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#2D2D2D]">
              {menus.filter(m => m.nivel === 3).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#2D2D2D]">
              {editingMenu ? 'Editar Menú' : 'Nueva Opción de Menú'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Ej: Dashboard, Configuración..."
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ruta">Ruta</Label>
              <Input
                id="ruta"
                value={formData.ruta}
                onChange={(e) => setFormData(prev => ({ ...prev, ruta: e.target.value }))}
                placeholder="Ej: /dashboard, /configuracion"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icono</Label>
                <Select
                  value={formData.icono}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, icono: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(icon => {
                      const IconComp = iconMap[icon.value];
                      return (
                        <SelectItem key={icon.value} value={icon.value}>
                          <div className="flex items-center gap-2">
                            <IconComp size={16} />
                            <span>{icon.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Nivel</Label>
                <Select
                  value={formData.nivel.toString()}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, nivel: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Nivel 1 (Principal)</SelectItem>
                    <SelectItem value="2">Nivel 2 (Submenú)</SelectItem>
                    <SelectItem value="3">Nivel 3 (Subopción)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.nivel > 1 && (
              <div className="space-y-2">
                <Label>Menú Padre</Label>
                <Select
                  value={formData.padre_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, padre_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar menú padre" />
                  </SelectTrigger>
                  <SelectContent>
                    {parentMenusForSelect.map(parent => (
                      <SelectItem key={parent.id} value={parent.id}>
                        {parent.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Roles Permitidos</Label>
              <div className="flex flex-wrap gap-2">
                {rolesOptions.map(role => (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => handleRolesChange(role.value)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      formData.roles_permitidos.includes(role.value)
                        ? 'bg-[#9d8272] text-white'
                        : 'bg-[#F5F5F5] text-[#5A5A5A] hover:bg-[#E8E8E7]'
                    }`}
                  >
                    {role.label}
                  </button>
                ))}
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || !formData.nombre}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
              >
                {saving ? 'Guardando...' : editingMenu ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#2D2D2D] flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={24} />
              Confirmar Eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-[#5A5A5A]">
              ¿Estás seguro de eliminar el menú <strong>"{menuToDelete?.nombre}"</strong>?
            </p>
            <p className="text-sm text-[#888888] mt-2">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {saving ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Menus;
